import { createFocusTrap, useClickOutside, useEscapeKeydown, usePortal, } from '../../internal/actions/index.js';
import { addMeltEventListener, builder, createElHelpers, effect, executeCallbacks, generateIds, getPortalDestination, handleFocus, isBrowser, isHTMLElement, kbd, last, noop, overridable, removeScroll, sleep, styleToString, toWritableStores, } from '../../internal/helpers/index.js';
import { tick } from 'svelte';
import { derived, get, writable } from 'svelte/store';
import { omit } from '../../internal/helpers/object';
const { name } = createElHelpers('dialog');
const defaults = {
    preventScroll: true,
    closeOnEscape: true,
    closeOnOutsideClick: true,
    role: 'dialog',
    defaultOpen: false,
    portal: 'body',
    forceVisible: false,
    openFocus: undefined,
    closeFocus: undefined,
};
const openDialogIds = writable([]);
export const dialogIdParts = ['content', 'title', 'description'];
export function createDialog(props) {
    const withDefaults = { ...defaults, ...props };
    const options = toWritableStores(omit(withDefaults, 'ids'));
    const { preventScroll, closeOnEscape, closeOnOutsideClick, role, portal, forceVisible, openFocus, closeFocus, } = options;
    const activeTrigger = writable(null);
    const ids = toWritableStores({
        ...generateIds(dialogIdParts),
        ...withDefaults.ids,
    });
    const openWritable = withDefaults.open ?? writable(withDefaults.defaultOpen);
    const open = overridable(openWritable, withDefaults?.onOpenChange);
    const isVisible = derived([open, forceVisible], ([$open, $forceVisible]) => {
        return $open || $forceVisible;
    });
    let unsubScroll = noop;
    function handleOpen(e) {
        const el = e.currentTarget;
        const triggerEl = e.currentTarget;
        if (!isHTMLElement(el) || !isHTMLElement(triggerEl))
            return;
        open.set(true);
        activeTrigger.set(triggerEl);
    }
    function handleClose() {
        open.set(false);
        handleFocus({
            prop: get(closeFocus),
            defaultEl: get(activeTrigger),
        });
    }
    effect([open], ([$open]) => {
        // Prevent double clicks from closing multiple dialogs
        sleep(100).then(() => {
            if ($open) {
                openDialogIds.update((prev) => {
                    prev.push(get(ids.content));
                    return prev;
                });
            }
            else {
                openDialogIds.update((prev) => prev.filter((id) => id !== get(ids.content)));
            }
        });
    });
    const trigger = builder(name('trigger'), {
        stores: [open, ids.content],
        returned: ([$open, $contentId]) => {
            return {
                'aria-haspopup': 'dialog',
                'aria-expanded': $open,
                'aria-controls': $contentId,
                type: 'button',
            };
        },
        action: (node) => {
            const unsub = executeCallbacks(addMeltEventListener(node, 'click', (e) => {
                handleOpen(e);
            }), addMeltEventListener(node, 'keydown', (e) => {
                if (e.key !== kbd.ENTER && e.key !== kbd.SPACE)
                    return;
                e.preventDefault();
                handleOpen(e);
            }));
            return {
                destroy: unsub,
            };
        },
    });
    const overlay = builder(name('overlay'), {
        stores: [isVisible],
        returned: ([$isVisible]) => {
            return {
                hidden: $isVisible ? undefined : true,
                tabindex: -1,
                style: styleToString({
                    display: $isVisible ? undefined : 'none',
                }),
                'aria-hidden': true,
                'data-state': $isVisible ? 'open' : 'closed',
            };
        },
        action: (node) => {
            let unsubEscapeKeydown = noop;
            if (get(closeOnEscape)) {
                const escapeKeydown = useEscapeKeydown(node, {
                    handler: () => {
                        handleClose();
                    },
                });
                if (escapeKeydown && escapeKeydown.destroy) {
                    unsubEscapeKeydown = escapeKeydown.destroy;
                }
            }
            return {
                destroy() {
                    unsubEscapeKeydown();
                },
            };
        },
    });
    const content = builder(name('content'), {
        stores: [isVisible, ids.content, ids.description, ids.title],
        returned: ([$isVisible, $contentId, $descriptionId, $titleId]) => {
            return {
                id: $contentId,
                role: get(role),
                'aria-describedby': $descriptionId,
                'aria-labelledby': $titleId,
                'data-state': $isVisible ? 'open' : 'closed',
                tabindex: -1,
                hidden: $isVisible ? undefined : true,
                style: styleToString({
                    display: $isVisible ? undefined : 'none',
                }),
            };
        },
        action: (node) => {
            let activate = noop;
            let deactivate = noop;
            const destroy = executeCallbacks(effect([open], ([$open]) => {
                if (!$open)
                    return;
                const focusTrap = createFocusTrap({
                    immediate: false,
                    escapeDeactivates: false,
                    returnFocusOnDeactivate: false,
                    fallbackFocus: node,
                });
                activate = focusTrap.activate;
                deactivate = focusTrap.deactivate;
                const ac = focusTrap.useFocusTrap(node);
                if (ac && ac.destroy) {
                    return ac.destroy;
                }
                else {
                    return focusTrap.deactivate;
                }
            }), effect([closeOnOutsideClick, open], ([$closeOnOutsideClick, $open]) => {
                return useClickOutside(node, {
                    enabled: $open,
                    handler: (e) => {
                        if (e.defaultPrevented)
                            return;
                        const $openDialogIds = get(openDialogIds);
                        const isLast = last($openDialogIds) === get(ids.content);
                        if ($closeOnOutsideClick && isLast) {
                            handleClose();
                        }
                    },
                }).destroy;
            }), effect([closeOnEscape], ([$closeOnEscape]) => {
                if (!$closeOnEscape)
                    return noop;
                const escapeKeydown = useEscapeKeydown(node, {
                    handler: () => {
                        handleClose();
                    },
                });
                if (escapeKeydown && escapeKeydown.destroy) {
                    return escapeKeydown.destroy;
                }
                return noop;
            }), effect([isVisible], ([$isVisible]) => {
                tick().then(() => {
                    if (!$isVisible) {
                        deactivate();
                    }
                    else {
                        activate();
                    }
                });
            }));
            return {
                destroy: () => {
                    unsubScroll();
                    destroy();
                },
            };
        },
    });
    const portalled = builder(name('portalled'), {
        stores: portal,
        returned: ($portal) => ({
            'data-portal': $portal ? '' : undefined,
        }),
        action: (node) => {
            const unsubPortal = effect([portal], ([$portal]) => {
                if (!$portal)
                    return noop;
                const portalDestination = getPortalDestination(node, $portal);
                if (portalDestination === null)
                    return noop;
                const portalAction = usePortal(node, portalDestination);
                if (portalAction && portalAction.destroy) {
                    return portalAction.destroy;
                }
                else {
                    return noop;
                }
            });
            return {
                destroy() {
                    unsubPortal();
                },
            };
        },
    });
    const title = builder(name('title'), {
        stores: [ids.title],
        returned: ([$titleId]) => ({
            id: $titleId,
        }),
    });
    const description = builder(name('description'), {
        stores: [ids.description],
        returned: ([$descriptionId]) => ({
            id: $descriptionId,
        }),
    });
    const close = builder(name('close'), {
        returned: () => ({
            type: 'button',
        }),
        action: (node) => {
            const unsub = executeCallbacks(addMeltEventListener(node, 'click', () => {
                handleClose();
            }), addMeltEventListener(node, 'keydown', (e) => {
                if (e.key !== kbd.SPACE && e.key !== kbd.ENTER)
                    return;
                e.preventDefault();
                handleClose();
            }));
            return {
                destroy: unsub,
            };
        },
    });
    effect([open, preventScroll], ([$open, $preventScroll]) => {
        if (!isBrowser)
            return;
        if ($preventScroll && $open)
            unsubScroll = removeScroll();
        if ($open) {
            const contentEl = document.getElementById(get(ids.content));
            handleFocus({ prop: get(openFocus), defaultEl: contentEl });
        }
        return () => {
            // we only want to remove the scroll lock if the dialog is not forced visible
            // otherwise the scroll removal is handled in the `destroy` of the `content` builder
            if (!get(forceVisible)) {
                unsubScroll();
            }
        };
    });
    return {
        ids,
        elements: {
            content,
            trigger,
            title,
            description,
            overlay,
            close,
            portalled,
        },
        states: {
            open,
        },
        options,
    };
}
